import { apiClient, apiEndpoint } from '../../../../services/api.services';
import { GenerateResult, IdentifierAxisUsage, IdentifierRow, ProductIdentifierView, RetailPreviewResult } from '../types';

/** What a save reports back, including the outcome of any Retail Identifier claim. */
export interface SaveResult {
  saved: number;
  retailClaimed?: number;
  retailShortfall?: number;
  packagingRecorded?: number;
  packagingConflicts?: number;
  packagingCleared?: number;
  manualRecorded?: number;
  manualRefused?: number;
}

/**
 * The single API surface for the Product Identifier feature (BM-9580 requirement 12).
 *
 * Every read and write for this module goes through here — no component, context or thunk builds
 * a URL of its own. Importable from anywhere, with no Redux dependency, so the same functions
 * serve the page, the grid and any future consumer.
 *
 * Mirrors one REST resource:
 *   GET    /api/v1/dev-orders/:devOrderId/product-identifiers
 *   POST   /api/v1/dev-orders/:devOrderId/product-identifiers/generate
 *   PUT    /api/v1/dev-orders/:devOrderId/product-identifiers
 *   POST   /api/v1/dev-orders/:devOrderId/product-identifiers/rows
 *   DELETE /api/v1/dev-orders/:devOrderId/product-identifiers/rows/:rowId
 *   POST   /api/v1/dev-orders/:devOrderId/product-identifiers/retail-preview
 *   GET    /api/v1/dev-orders/:devOrderId/product-identifiers/axis-usage
 */

/** Builds the resource path for one dev order. */
function resourceUrl(devOrderId: string): string {
  return `${apiEndpoint}/v1/dev-orders/${devOrderId}/product-identifiers`;
}

/**
 * The response envelope produced by `setSuccessResponse` on the server.
 *
 * NOTE: `apiClient` has a response interceptor that already returns `response.data`
 * (`services/api.services.js:88`), so what resolves here is the BODY, not the axios response.
 * Reaching for `response.data.data` therefore reads one level too deep and yields undefined.
 */
interface ApiEnvelope<T> {
  code?: number;
  message?: string;
  data?: T;
}

/** Unwraps the `{ code, message, data }` body. */
function unwrap<T>(response: ApiEnvelope<T>): T | undefined {
  return response?.data;
}

/** Fetches columns, size-range tabs and saved rows for a dev order. */
export async function fetchProductIdentifiers(devOrderId: string): Promise<ProductIdentifierView | undefined> {
  const response = await apiClient.get(resourceUrl(devOrderId));
  return unwrap<ProductIdentifierView>(response);
}

/**
 * Builds combinations from the product's current selections and assigns auto-ID values.
 * Does not persist — call {@link saveProductIdentifiers} with the result.
 */
export async function generateProductIdentifiers(devOrderId: string): Promise<GenerateResult | undefined> {
  const response = await apiClient.post(`${resourceUrl(devOrderId)}/generate`, {});
  return unwrap<GenerateResult>(response);
}

/**
 * Replaces the saved row set.
 *
 * `rows` is sent whole, so any `retailCells` a pull put on a row travel with it. The
 * server claims those barcodes as part of this call and reports how many combinations the
 * pool could not cover.
 */
export async function saveProductIdentifiers(
  devOrderId: string,
  rows: ReadonlyArray<IdentifierRow>,
  identifierType: 'auto' | 'manual',
  latestCounterValues?: Record<string, Record<string, string>>,
): Promise<SaveResult | undefined> {
  const response = await apiClient.put(resourceUrl(devOrderId), {
    rows,
    identifierType,
    latestCounterValues,
  });
  return unwrap<SaveResult>(response);
}

/**
 * Asks which barcodes this product would draw from one Retail Identifier library.
 *
 * Writes nothing — the pool is claimed on save — so previewing and navigating away costs
 * no numbers. Only combinations with no barcode yet are offered one.
 */
export async function previewRetailBarcodes(
  devOrderId: string,
  libraryId: string,
): Promise<RetailPreviewResult | undefined> {
  const response = await apiClient.post(`${resourceUrl(devOrderId)}/retail-preview`, { libraryId });
  return unwrap<RetailPreviewResult>(response);
}

/** Appends rows to the saved set. */
export async function appendProductIdentifierRows(
  devOrderId: string,
  rows: ReadonlyArray<IdentifierRow>,
): Promise<{ appended: number } | undefined> {
  const response = await apiClient.post(`${resourceUrl(devOrderId)}/rows`, { rows });
  return unwrap<{ appended: number }>(response);
}

/** Removes one saved row by its subdocument id. */
export async function deleteProductIdentifierRow(
  devOrderId: string,
  rowId: string,
): Promise<{ deleted: boolean } | undefined> {
  const response = await apiClient.delete(`${resourceUrl(devOrderId)}/rows/${rowId}`);
  return unwrap<{ deleted: boolean }>(response);
}

/**
 * Reads which Size, Colourway, Variable 1 and Variable 2 values the product's saved rows use, so
 * the Product Information edit page can warn before a change deletes identifier rows (BM-9211).
 */
export async function fetchProductIdentifierAxisUsage(devOrderId: string): Promise<IdentifierAxisUsage | undefined> {
  const response = await apiClient.get(`${resourceUrl(devOrderId)}/axis-usage`);
  return unwrap<IdentifierAxisUsage>(response);
}
